import type { GoogleAnalyticsCredentials } from '../types';

export type GoogleAnalyticsCredentialsUpdate = {
  measurementId?: string | null;
  apiSecret?: string | null;
};

const hasValue = (value: string | null | undefined): value is string => Boolean(value?.trim());

/**
 * Masked integration responses are display-only values. They prove that a
 * credential exists, but must never be sent back as a replacement secret.
 */
const isMaskedValue = (value: string): boolean => value.toLowerCase().includes('xxxxx');

export const isGoogleAnalyticsServerSideConfigured = (
  credentials: Partial<GoogleAnalyticsCredentials> | null | undefined,
): boolean => hasValue(credentials?.measurementId) && hasValue(credentials?.apiSecret);

export const prepareGoogleAnalyticsCredentials = (
  values: Partial<Record<keyof GoogleAnalyticsCredentialsUpdate, string | null | undefined>>,
): GoogleAnalyticsCredentialsUpdate => {
  const update: GoogleAnalyticsCredentialsUpdate = {};

  for (const field of ['measurementId', 'apiSecret'] as const) {
    const value = values[field];

    // Undefined means the field was not changed. Empty means explicitly clear it.
    if (value === undefined) continue;
    if (typeof value === 'string' && isMaskedValue(value)) continue;
    update[field] = typeof value === 'string' && !value.trim() ? null : value;
  }

  return update;
};
